// features/videos/WatchRail.tsx — the video watch page's "up next" rail: a
// Trending-now group (ranked, ascending) + a Suggested group (category/tag
// affinity, then recency). Hidden entirely in theater mode by the caller.
import styled from 'styled-components';
import { RailVideoItem } from './RailVideoItem';
import { trendingForRail, suggestedForRail } from './railRanking';
import type { VideoItem, VideoCreator } from '@/core/models/video';
import { RailGroup, RailPanel } from '@/app/shell/RightRail';

const Wrap = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const GroupHeading = styled.h3`
  margin: 14px 0 4px;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 700;
  color: ${({ theme }) => theme.color.textPrimary};
`;

function creatorMap(creators: VideoCreator[]): Record<string, VideoCreator> {
  const map: Record<string, VideoCreator> = {};
  for (const c of creators) map[c.id] = c;
  return map;
}

export interface WatchRailProps {
  current: VideoItem;
  videos: VideoItem[];
  creators: VideoCreator[];
}

export function WatchRail({ current, videos, creators }: WatchRailProps) {
  const trending = trendingForRail(videos, current.id);
  const suggested = suggestedForRail(videos, current, trending);
  const byId = creatorMap(creators);

  return (
    <Wrap aria-label="Up next">
      <RailGroup pageKey="video-watch" headerTitle="Up next">
        {trending.length > 0 && (
          <RailPanel id="trending" label="Trending now">
            <div>
              <GroupHeading>Trending now</GroupHeading>
              {trending.map((v) => (
                <RailVideoItem key={v.id} video={v} creator={byId[v.authorId]} />
              ))}
            </div>
          </RailPanel>
        )}

        {suggested.length > 0 && (
          <RailPanel id="suggested" label="Suggested">
            <div>
              <GroupHeading>Suggested</GroupHeading>
              {suggested.map((v) => (
                <RailVideoItem key={v.id} video={v} creator={byId[v.authorId]} />
              ))}
            </div>
          </RailPanel>
        )}
      </RailGroup>
    </Wrap>
  );
}
